import type { IntlShape } from 'react-intl';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import type { CardContext } from '@atlaskit/link-provider/context';
import type { EmojiNodeDataProvider } from '@atlaskit/editor-plugin-emoji/EmojiNodeDataProvider';
import type { UserPreferencesProvider as UserPreferencesProviderNext } from '@atlaskit/editor-common/user-preferences-provider';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import type { ContextualFormattingEnabledOptions } from '@atlaskit/editor-common/toolbar';
import type { GetPMNodeHeight } from '@atlaskit/editor-common/extensibility/types';
import type { FindReplaceOptions } from '@atlaskit/editor-plugins/find-replace';
import type { TaskDecisionProvider } from '@atlaskit/task-decision/types';
import type { MediaPluginOptions } from '@atlaskit/editor-plugins/media';
import type { ViewMode } from '@atlaskit/editor-plugin-editor-viewmode/editorViewmodePluginType';
import type { PasteOptionsToolbarPluginConfiguration } from '@atlaskit/editor-plugin-paste-options-toolbar/paste-options-toolbar-plugin-type';
import type { EmojiProvider } from '@atlaskit/emoji';
import type { MentionProvider } from '@atlaskit/mention/resource';
import type { ContextIdentifierProvider } from '@atlaskit/editor-common/provider-factory/context-identifier-provider';
import type { AutoformattingProvider } from '@atlaskit/editor-common/provider-factory/autoformatting-provider';
import type { QuickInsertItem } from '@atlaskit/editor-common/provider-factory/quick-insert-provider';
import type { MentionPluginConfig } from '@atlaskit/editor-plugins/mentions';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions/extension-handler';
import type { EventHandlers } from '@atlaskit/editor-common/EventHandlers';
import type { ExtensionPluginOptions } from '@atlaskit/editor-plugin-extension/extensionPluginType';
import type { LimitedModePluginOptions } from '@atlaskit/editor-plugin-limited-mode/limited-mode-plugin-type';
import type { LoomPluginOptionsWithoutProvider } from '@atlaskit/editor-plugin-loom/types';
import type { MentionsChangedHandler } from '@atlaskit/editor-plugin-mentions/types';
import type { EngagementPlatformPluginOptions } from '@atlaskit/editor-plugin-engagement-platform';
import type { ContextPanelPluginOptions } from '@atlaskit/editor-plugin-context-panel/contextPanelPluginType';
import type { SelectionExtensionPluginOptions } from '@atlaskit/editor-plugin-selection-extension/types';
import type { DiffParams } from '@atlaskit/editor-plugin-show-diff/show-diff-plugin-type';
import type { SyncedBlockProvider } from '@atlaskit/editor-synced-block-provider/syncBlockProvider';
import { getSyncedBlockRenderer } from '@atlaskit/editor-synced-block-renderer/get-synced-block-renderer';
import type { UserPreferencesProvider } from '@atlaskit/editor-common/types/user-preferences';
import type { LinkingOptions } from '@atlaskit/editor-common/types/hyperlink';
import type { EmptyStateHandler } from '@atlaskit/editor-common/types/empty-state-handler';
import type { QuickInsertOptions } from '@atlaskit/editor-common/types/quick-insert';
import type { EditorContentMode } from '@atlaskit/editor-common/types/editor-appearance';
import type { AnnotationProviders } from '@atlaskit/editor-plugins/annotation';
import type { CollabEditOptions } from '@atlaskit/editor-common/collab';
import type { TrackChangesPluginOptions } from '@atlaskit/editor-plugin-track-changes/track-changes-plugin-type';
import { mediaEditingPlugin } from '@atlaskit/editor-plugin-media-editing/media-editing-plugin';

import { fullPagePreset, type FullPagePresetProps } from '@atlassian/confluence-presets/full-page';
import type { DuplicateMauiAppSuccess } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions/duplicate-maui-app';
import { markdownModePlugin } from '@atlassian/editor-plugin-markdown-mode';
import type { MarkdownModePluginOptions } from '@atlassian/editor-plugin-markdown-mode/types';
import type { SerializeFileMedia } from '@atlassian/mdast-util-from-pm/handlers/media';
import { isMarkdownCompatibleQuickInsertItem } from '@atlassian/editor-plugin-markdown-mode/markdown-compatible-quick-insert-items';
import {
	isMarkdownCompatibleToolbarComponent,
	isMarkdownCompatibleToolbarItem,
} from '@atlassian/editor-plugin-markdown-mode/markdown-compatibility';
import { nativeEmbedsPlugin } from '@atlassian/editor-plugin-native-embeds/native-embeds-plugin';
import type { AIDefinitionsPluginOptions } from '@atlassian/editor-plugin-ai-definitions/types';
import type { AIPluginOptions } from '@atlassian/editor-plugin-ai/aiPluginType';
import { aiAutocompletePlugin } from '@atlassian/editor-plugin-ai-autocomplete/ai-autocomplete-plugin';
import type { AiSuggestionsPluginOptions } from '@atlassian/editor-plugin-ai-suggestions';
import { malleableUiPlugin } from '@atlassian/editor-plugin-malleable-ui/malleableUiPlugin';
import { companyHubSectionCtaPlugin } from '@atlassian/editor-plugin-company-hub-section-cta/companyHubSectionCtaPlugin';
import type { ReferentialityContext } from '@atlassian/editor-referentiality/types';
import { forgeEditorPlugin } from '@atlassian/xen-editor-provider/editor-plugin';

import type { FullPageAppearances } from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import { CompetitorPrompt } from '@confluence/prompt-competitor-link/entry-points/PromptPopup.ts';
import { CREATE_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { getExperienceTracker } from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import { createInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/createInlineCommentCompoundExperience.ts';
import { selectInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience.ts';
import { nativeTabsToolbarPlugin } from '@confluence/editor-plugin-native-tabs-toolbar/entry-points/nativeTabsToolbarPlugin.ts';
import { legacyContentMacroPlugin } from '@confluence/experiment-legacy-content-macro-plugin';
import {
	readStoredMarkdownViewMode,
	writeStoredMarkdownViewMode,
} from '@confluence/content-markdown-mode/entry-points/markdownViewMode.ts';
import {
	expVal,
	expValEquals,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { getUnsupportedContent } from '@confluence/fabric-extension-lib/entry-points/getUnsupportedContent.ts';
import { fabricExtensionManagerPlugin } from '@confluence/fabric-extension-manager-plugin';
import { fg } from '@confluence/feature-gating';
import { editorOfflineStoragePlugin } from '@confluence/editor-offline-storage/entry-points/editorOfflineStoragePlugin.ts';
import { EditorHeadingLinksPlugin } from '@confluence/editor-heading-links/entry-points/headingLinksPlugin.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { createRemixContentComponent } from '@confluence/malleable-ui-common/entry-points/EditorRemixCreator.ts';
import { isSSRStreaming } from '@confluence/ssr-utilities/entry-points/isSSRStreaming.ts';

import { createConfluenceMauiDiagramPasteConfig } from '../utils/createConfluenceMauiDiagramPasteConfig.ts';
import { getCodeBlockFormatProvider } from '../utils/codeBlockFormatProvider.ts';

import { conflictResolutionUIPlugin } from './plugins/conflictResolutionUIPlugin/index.ts';
import { emptyStatePlugin } from './plugins/emptyStatePlugin.ts';
import { ssrPlugin } from './plugins/ssrPlugin.ts';
import { pageTitleUtilsPlugin } from './plugins/pageTitleUtilsPlugin.ts';

/**
 * Wraps a consumer-supplied `QuickInsertOptions` with a Markdown-Mode
 * allowlist filter when the page is in Markdown Mode. Items whose `id` is
 * not on `MARKDOWN_COMPATIBLE_QUICK_INSERT_ITEM_IDS` (panels, status,
 * layouts, smart cards, media, etc.) are hidden from the `/` typeahead.
 * Composes with any caller-supplied filter via `&&`. No-op when not in
 * Markdown Mode. Exported for unit testing.
 */
export const applyMarkdownModeQuickInsertFilter = (
	quickInsert: QuickInsertOptions | undefined,
	isMarkdownMode: boolean,
): QuickInsertOptions | undefined => {
	if (!isMarkdownMode) {
		return quickInsert;
	}

	// Normalise to an object so we can spread + add the filter.
	const baseOptions: Exclude<QuickInsertOptions, boolean> =
		typeof quickInsert === 'object' && quickInsert !== null ? quickInsert : {};

	const callerFilter = baseOptions.itemFilter;
	const itemFilter: NonNullable<typeof callerFilter> = callerFilter
		? (item: QuickInsertItem) => isMarkdownCompatibleQuickInsertItem(item) && callerFilter(item)
		: isMarkdownCompatibleQuickInsertItem;

	return { ...baseOptions, itemFilter };
};

/**
 * IMPORTANT: Don't use optional properties in this interface because it reduces the type safety.
 *            Use `undefined` instead to indicate that the property is not set.
 */
export interface CreateFullPageEditorPresetProps {
	__isRemixHidden?: boolean;
	__remixDisabledReason: 'archived' | 'version-history' | undefined;
	__livePage: boolean | undefined;
	__rendererExtensionOptions: ExtensionPluginOptions['__rendererExtensionOptions'] | undefined;
	allowFindReplace: boolean | FindReplaceOptions | undefined;
	annotationProviders: Pick<AnnotationProviders, 'annotationManager' | 'inlineComment'> | undefined;
	appearance: FullPageAppearances;
	autoformattingProvider: Promise<AutoformattingProvider> | undefined;
	collabEdit: CollabEditOptions | undefined;
	contentId: string | undefined;
	/** Whether this page is a Markdown Mode page. */
	isMarkdownMode: boolean | undefined;
	pasteOptionsToolbarPluginOptions: PasteOptionsToolbarPluginConfiguration | undefined;
	spaceKey: string | undefined;
	contextIdentifierProvider: Promise<ContextIdentifierProvider> | undefined;
	contextPanelOptions: Pick<ContextPanelPluginOptions, 'objectSideBar'> | undefined;
	editorEngagementPlatformPluginConfig: Pick<EngagementPlatformPluginOptions, 'coordinationClient'>;
	editorPluginAIDefinitionsConfig: AIDefinitionsPluginOptions;
	editorPluginAIDefinitionsEnabled: boolean;
	editorPluginAIOptions?: AIPluginOptions;
	elementBrowser:
		| {
				emptyStateHandler?: EmptyStateHandler;
		  }
		| undefined;
	emojiProvider: Promise<EmojiProvider> | undefined;
	emojiNodeDataProvider: EmojiNodeDataProvider | undefined;
	extensionHandlers: ExtensionHandlers | undefined;
	flags: FlagsStateContainer | undefined;
	getExtensionHeight: GetPMNodeHeight | undefined;
	getHeadingLinkPath: () => string | null;
	shouldHideHeadingAnchorLinks?: boolean;
	handleMentionsChanged: MentionsChangedHandler;
	currentUserId: string | undefined;
	getIsRovoPanelOpen?: () => boolean;
	hasEditPermission: boolean | undefined;
	hasRequestedEditPermission: boolean | undefined;
	intl: IntlShape;
	isAIEnabledForContent: boolean;
	isCompanyHub: boolean;
	isDesktop: boolean;
	isLoomPluginEnabled: boolean;
	limitedModePluginOptions: LimitedModePluginOptions | undefined;
	smartCardContext?: CardContext;
	enableAgentSectioning?: boolean;
	linking: Pick<LinkingOptions, 'linkPicker' | 'smartLinks'> | undefined;
	loomPluginOptions: Pick<LoomPluginOptionsWithoutProvider, 'renderButton'>;
	media:
		| Pick<
				MediaPluginOptions,
				'provider' | 'syncProvider' | 'mediaViewerExtensions' | 'fallbackMediaNameFetcher'
		  >
		| undefined;
	mention: Pick<MentionPluginConfig, 'profilecardProvider'> | undefined;
	mentionProvider: Promise<MentionProvider> | undefined;
	prevAppearance: FullPageAppearances | undefined;
	quickInsert: QuickInsertOptions | undefined;
	referentialityContext: ReferentialityContext;
	requestToEditContent: (() => void) | undefined;
	selectionExtensionPluginOptions: SelectionExtensionPluginOptions;
	taskDecisionProvider: Promise<TaskDecisionProvider> | undefined;
	userPreferencesProvider: UserPreferencesProvider | undefined;
	userPreferencesProviderNext: UserPreferencesProviderNext | undefined;
	viewMode: ViewMode | undefined;
	contextualFormattingEnabled: boolean;
	contextualFormattingMode?: ContextualFormattingEnabledOptions;
	disabled?: boolean;
	blockLinkHashPrefix: string | undefined;
	showDiffPluginParams: DiffParams | undefined;
	syncedBlockProvider: SyncedBlockProvider | undefined;
	syncedBlockEventHandlers: EventHandlers | undefined;
	disableFloatingToolbar: boolean | undefined;
	disableToolbar: boolean | undefined;
	trackChangesPluginOptions: TrackChangesPluginOptions | undefined;
	contentMode: EditorContentMode | undefined;
	aiSuggestionsPluginOptions: AiSuggestionsPluginOptions;
	enablePlaceholderLoadingSpinner?: boolean;
	isEmbeddedConfluence: boolean;
	isRovoLLMEnabled: boolean;
	duplicateMauiApp?: (
		appId: string,
		containerContentId: string,
		contentType?: string,
	) => Promise<DuplicateMauiAppSuccess>;
	contentType?: string;
	cloudId?: string;
	/**
	 * Resolves `media (type: "file")` ids to a public proxy URL for use by
	 * the markdown-mode source view. Supplied by the Confluence wrapper via
	 * `useFileMediaUrlSerializer`. Pass `undefined` to disable file media in
	 * markdown mode (it falls back to alt-text-only).
	 */
	serializeFileMediaForMarkdown?: SerializeFileMedia;
	onSourceWritebackError?: MarkdownModePluginOptions['onSourceWritebackError'];
}

/**
 * This function creates a full page editor preset with the provided options.
 *
 * If you want to do any changes, first of all, read https://hello.atlassian.net/wiki/spaces/EDITOR/pages/4239829169/Editor+RFC+059+Editor+Presets+Package.
 *
 * HOW TO MAKE CHANGES:
 * 1. Do not use optional properties in the `CreateFullPageEditorPresetProps` interface.
 * 2. Keep this function as small and simple as possible. Pass only the necessary parameters to the plugin options:
 *   - If a plugin parameter doesn't need any data from Confluence (e.g. option is just a static value),
 *     define it directly in platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/{pluginName}Options.ts.
 *   - If a plugin parameter depends on Statsig experiment value, FG value,
 *     define it directly in platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/{pluginName}Options.ts.
 *   - Pass parameters from this function to the plugin options ONLY if there is NO OTHER WAY to do it directly in the plugin options.
 *   - Pass only NECESSARY parameters to the plugin options, AVOID passing the WHOLE OBJECT with all properties.
 *   - Do NOT USE SPREAD operator (`...props`) to pass plugin options, it can make the code less readable and harder to maintain.
 */
// FIXME: https://product-fabric.atlassian.net/browse/ED-25722

type FullPagePluginOptionsWithOptionalAI = Omit<FullPagePresetProps['pluginOptions'], 'ai'> & {
	ai?: FullPagePresetProps['pluginOptions']['ai'];
};

const allowOptionalAIPluginOptions = (
	pluginOptions: FullPagePluginOptionsWithOptionalAI,
): FullPagePresetProps['pluginOptions'] => pluginOptions as FullPagePresetProps['pluginOptions'];

type FullPageProvidersWithFormatCodeProvider = FullPagePresetProps['providers'] & {
	formatCodeProvider?: ReturnType<typeof getCodeBlockFormatProvider>;
};

const allowFormatCodeProvider = (
	providers: FullPageProvidersWithFormatCodeProvider,
): FullPagePresetProps['providers'] => providers as FullPagePresetProps['providers'];

export function createFullPageEditorPreset(options: CreateFullPageEditorPresetProps): any {
	const { intl, appearance, prevAppearance } = options;

	const isNativeEmbeddedLiveDoc =
		!!options.__livePage &&
		!!options.isEmbeddedConfluence &&
		UNSAFE_expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true);

	const isMarkdownMode = options.isMarkdownMode ?? false;
	const isEditorAIPluginEnabled = Boolean(options.editorPluginAIOptions);
	const quickInsertOptions = applyMarkdownModeQuickInsertFilter(
		options.quickInsert,
		isMarkdownMode,
	);
	const agentManagedExtensionPluginOption = {
		agentManagedExtension: {
			cloudId: options.cloudId,
			contentType: options.contentType,
		},
	};

	// `prettier-ignore` keeps the long fluent chain below on its current
	// `return fullPagePreset(...)` form. Without it Prettier re-indents ~570
	// chained-method lines and explodes the diff for any unrelated change.
	// The chain is purely declarative configuration.
	// prettier-ignore
	return (
		fullPagePreset({
			intl,
			isRovoLLMEnabled: options.isRovoLLMEnabled,
			pluginOptions: allowOptionalAIPluginOptions({
				contentFormat: {
					initialContentMode: options.contentMode ?? 'standard',
				},
				base: {
					__livePage: options.__livePage,
				},
				helpDialog: {
					imageUploadProviderExists: false,
					aiEnabled: options.isAIEnabledForContent ?? false,
				},
				hyperlink: {
					editorAppearance: appearance,
					linkPicker: options.linking?.linkPicker,
					onClickCallback: options.linking?.smartLinks?.onClickCallback,
				},
				quickInsert: {
					emptyStateHandler: options.elementBrowser?.emptyStateHandler,
					quickInsert: quickInsertOptions,
				},
				placeholder: {
					viewMode: options.viewMode,
					isAIEnabled: options.isAIEnabledForContent ?? false,
					// hide placeholder when (1) variant1 is enabled (2) not embedded editor & (3) user would see BPQA.
					// match BPQA conditions as defined by shouldRenderBPQA in FullPageEditorComponent
					// in this case, only isCompanyHub is not handled yet by the placeholder plugin
					isPlaceholderHidden:
						!options.isEmbeddedConfluence &&
						!options.isCompanyHub &&
						options.isRovoLLMEnabled &&
						expValEquals('cwr_blank_object_experiment', 'cohort', 'variant1'),
					enableLoadingSpinner: options.enablePlaceholderLoadingSpinner,
					isRovoLLMEnabled: options.isRovoLLMEnabled,
				},
				selection: {
					__livePage: options.__livePage,
				},
				...agentManagedExtensionPluginOption,
				editorDisabled: {
					disabled: options.disabled,
				},
				breakout: {
					editorAppearance: appearance,
				},
				expand: {
					editorAppearance: appearance,
					__livePage: options.__livePage,
				},
				annotation: {
					annotationManager: options.annotationProviders?.annotationManager,
					createCommentExperience: createInlineCommentCompoundExperience,
					selectCommentExperience: selectInlineCommentCompoundExperience,
					viewInlineCommentTraceUFOPress: () => {
						// Similar to logic in next/packages/annotation-provider-inline-comments/src/renderer/ViewComponent.tsx
						// but for editor:
						// Only start this trace if this is not viewing a just-created comment, in
						// which case an `editor-create-inline-comment` action trace would still be
						// running. We let it run to cover the entire create UI flow end-to-end.
						const createExperienceState = getExperienceTracker().getExperienceState(
							CREATE_INLINE_COMMENT_EXPERIENCE,
						);
						const skipTrace =
							createExperienceState?.hasStopped === false &&
							expValEquals('confluence_fe_create_comment_optimistic', 'isEnabled', true);
						if (!skipTrace) {
							traceUFOPress('view-inline-comment-action');
						}
					},
				},
				media: {
					createCommentExperience: fg('confluence_fe_create_inline_comment_exp_coverage_2')
						? createInlineCommentCompoundExperience
						: undefined,
					editorAppearance: appearance,
					mediaViewerExtensions: options.media?.mediaViewerExtensions,
					fallbackMediaNameFetcher: options.media?.fallbackMediaNameFetcher,
				},
				mentions: {
					enableAgentSectioning: options.enableAgentSectioning,
					handleMentionsChanged: options.handleMentionsChanged,
					currentUserId: options.currentUserId,
					...(expValEquals('platform_editor_agent_mentions', 'isEnabled', true) && { getIsRovoPanelOpen: options.getIsRovoPanelOpen }),
				},
				table: {
					__livePage: options.__livePage,
					editorAppearance: appearance,
					prevEditorAppearance: prevAppearance,
				},
				tasksAndDecisions: {
					hasEditPermission: options.hasEditPermission,
					requestToEditContent: options.requestToEditContent,
					hasRequestedEditPermission: options.hasRequestedEditPermission,
				},
				collabEdit: {
					collabEdit: options.collabEdit,
					__livePage: options.__livePage,
				},
				// Remove the options.contextPanelOptions undefined checking after cleaning `platform_editor_ai_object_sidebar_injection` FG
				// and options.contextPanelOptions will be always defined.
				contextPanel: options.contextPanelOptions
					? {
							objectSideBar: options.contextPanelOptions.objectSideBar,
						}
					: undefined,
				extension: {
					extensionHandlers: options.extensionHandlers,
					editorAppearance: appearance,
					__rendererExtensionOptions: options.__rendererExtensionOptions,
					getExtensionHeight: options.getExtensionHeight,
					getUnsupportedContent,
				},
				layout: {
					editorAppearance: appearance,
				},
				card: {
					smartCardContext:
						isSSRStreaming()
							? options.smartCardContext
							: undefined,
					editorAppearance: appearance,
					linkPicker: options.linking?.linkPicker,
					onClickCallback: options.linking?.smartLinks?.onClickCallback,
					__livePage: options.__livePage,
					CompetitorPrompt,
					enablePasteDisplayAsMenu: true,
				},
				insertBlock: {
					editorAppearance: appearance,
					// In Markdown Mode, hide toolbar insert items whose underlying node
					// has no clean GFM round-trip (panels, layouts, dates, status,
					// mentions, emoji, decisions, expand, placeholder text, rich `media`).
					itemFilter: isMarkdownMode ? isMarkdownCompatibleToolbarItem : undefined,
					toolbarButtons: {
						emoji: { enabled: true, showAt: 'lg' },
						insert: { enabled: true, showAt: 'lg' },
						layout: { enabled: true, showAt: 'lg' },
						media: { enabled: true, showAt: 'lg' },
						mention: { enabled: true, showAt: 'lg' },
						table: { enabled: true, showAt: 'md' },
						taskList: { enabled: true, showAt: 'lg' },
					},
				},
				avatarGroup: {
					// Avatars are moved to Confluence codebase for Edit in Context
					// When Edit in Context is enabled primaryToolbarComponents is undefined
					// For more details please check
					// https://hello.atlassian.net/wiki/spaces/PCG/pages/2851572180/Editor+toolbar+for+live+pages+and+edit+in+context+projects
					collabEdit: options.collabEdit,
				},
				codeBidiWarning: {
					editorAppearance: appearance,
				},
				referentiality: {
					referentialityContext: options.referentialityContext,
				},
				...(options.editorPluginAIOptions ? { ai: options.editorPluginAIOptions } : {}),
				pasteOptionsToolbar: options.pasteOptionsToolbarPluginOptions,
				loom: {
					renderButton: options.loomPluginOptions.renderButton,
				},
				editorViewMode: {
					viewMode: options.viewMode,
				},
				aiDefinitions: options.editorPluginAIDefinitionsConfig,
				selectionMarker: {
					__livePage: options.__livePage ?? false,
				},
				engagementPlatform: {
					coordinationClient: options.editorEngagementPlatformPluginConfig.coordinationClient,
				},
				selectionExtension: options.selectionExtensionPluginOptions,
				metrics:
					// eslint-disable-next-line confluence-feature-gating/no-preconditioning
					expValEquals('platform_editor_controls', 'cohort', 'variant1')
						? { userPreferencesProvider: options.userPreferencesProvider }
						: undefined,
				primaryToolbar: {
					contextualFormattingEnabled: options.contextualFormattingEnabled,
				},
				selectionToolbar: {
					contextualFormattingEnabled: options.contextualFormattingEnabled,
					// In embedded live doc, the toolbar is always unpinned and the pin option is hidden
					disablePin: isNativeEmbeddedLiveDoc,
				},
				userPreferencesPlugin: {
					// In embedded live doc, force the toolbar to start unpinned regardless of any saved user preference
					initialToolbarDockingPosition: isNativeEmbeddedLiveDoc ? 'none' : undefined,
				},
				blockMenu: {
					getLinkPath: options.getHeadingLinkPath,
					blockLinkHashPrefix: options.blockLinkHashPrefix,
				},
				showDiff: options.showDiffPluginParams,
				syncedBlock:
					options.syncedBlockProvider && expValEquals('platform_synced_block', 'isEnabled', true)
						? {
								__livePage: options.__livePage,
								enableSourceCreation: true,
								syncedBlockRenderer: getSyncedBlockRenderer({
									syncBlockRendererOptions: {
										eventHandlers: options.syncedBlockEventHandlers,
										stickyHeaders: {
											show: true,
											offsetTop: 0,
										},
									},
								}),
								syncBlockDataProvider: options.syncedBlockProvider,
							}
						: undefined,
				limitedMode: options.limitedModePluginOptions || {},
				toolbar: {
					contextualFormattingEnabled: options.contextualFormattingMode ?? 'controlled',
					// In Markdown Mode, filter the toolbar two ways:
					//
					//   1. In wysiwyg view: hide buttons / menus whose underlying
					//      mark or node has no clean GFM round-trip (text colour,
					//      highlight, alignment, underline, sub/super, mention,
					//      emoji, layout, media, synced block).
					//   2. In source / preview view: hide everything except the
					//      view-mode toggle itself, since the buttons would have
					//      no effect on the source / preview surfaces.
					//
					componentFilter: isMarkdownMode ? isMarkdownCompatibleToolbarComponent : undefined,
				},
				trackChanges: {
					ButtonWrapper: options.trackChangesPluginOptions?.ButtonWrapper,
				},
				aiSuggestions: options.aiSuggestionsPluginOptions,
				aiImageGeneration: {
					isRemixHidden: options.__isRemixHidden || isMarkdownMode,
					isRovoLLMEnabled: options.isRovoLLMEnabled,
				}
			}),
			providers: allowFormatCodeProvider({
				// In embedded live doc, we always want the toolbar to be unpinned regardless of saved preferences.
				// Passing undefined for both providers prevents any shared user preference state from affecting
				// the embedded live doc toolbar docking position.
				userPreferencesProvider: isNativeEmbeddedLiveDoc
					? undefined
					: options.userPreferencesProvider,
				userPreferencesProviderNext: isNativeEmbeddedLiveDoc
					? undefined
					: options.userPreferencesProviderNext,
				cardProvider: options.linking?.smartLinks?.provider,
				contextIdentifierProvider: options.contextIdentifierProvider,
				inlineCommentAnnotationProvider: options.annotationProviders?.inlineComment,
				mediaProvider: options.media?.provider,
				syncMediaProvider: options.media?.syncProvider,
				mentionProvider: options.mentionProvider,
				profilecardProvider: options.mention?.profilecardProvider,
				emojiProvider: options.emojiProvider,
				emojiNodeDataProvider: options.emojiNodeDataProvider,
				formatCodeProvider: getCodeBlockFormatProvider(),
				collabEditProvider: options.collabEdit?.provider,
				autoformattingProvider: options.autoformattingProvider,
				taskDecisionProvider: options.taskDecisionProvider,
			}),
			enabledOptionalPlugins: {
				agentManagedExtension:
					options.isRovoLLMEnabled &&
					expValEquals('agent-managed_blocks_mvp', 'isEnabled', true),
				limitedMode: false,
				findReplace: Boolean(options.allowFindReplace),
				referentiality: Boolean(options.referentialityContext.plugin),
				loom: options.isLoomPluginEnabled,
				aiDefinitions: isEditorAIPluginEnabled && options.editorPluginAIDefinitionsEnabled,
				aiImageGeneration:
					isEditorAIPluginEnabled &&
					expValEquals('cc_page_experiences_editor_image_generation', 'isEnabled', true) &&
					isFeatureEnabled('confluence', 'confluence-ai'),
				connectivity:
					options.isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false),
				metrics: fg('platform_editor_ease_of_use_metrics'),
				codeBlockAdvanced: true,
				selectionExtension: expValEquals('platform_editor_controls', 'cohort', 'variant1'),
				aiExperience: Boolean(
					isEditorAIPluginEnabled &&
						options.editorPluginAIOptions?.aiGlobalOptIn.status !== 'disabled',
				),
				interaction:
					Boolean(options?.__livePage) ||
					expValEquals('platform_editor_no_cursor_on_edit_page_init', 'isEnabled', true),
				userPreferences: fg('platform_editor_use_preferences_plugin'),
				trackChanges: fg('aifc_create_enabled'),
				showDiff:
					Boolean(options?.showDiffPluginParams) || fg('aifc_create_enabled'),
				toolbar:
					!options?.disableToolbar &&
					(fg('aifc_create_enabled') ||
						(isEditorAIPluginEnabled &&
							options.editorPluginAIOptions?.aiGlobalOptIn.status !== 'enabled' &&
							expValEquals('platform_editor_toolbar_aifc_confluence', 'isEnabled', true))),
				blockMenu: expValEquals('platform_editor_block_menu', 'isEnabled', true),
				localId: fg('platform_editor_adf_with_localid'),
				aiStreamingOrchestrator: fg('aifc_create_enabled'),
				syncedBlock: expValEquals('platform_synced_block', 'isEnabled', true),
				floatingToolbar: !options?.disableFloatingToolbar,
				codeBidiWarning: !expValEquals(
					'platform_editor_remove_bidi_char_warning',
					'isEnabled',
					true,
				),
				contentFormat: true,
				uiControlRegistry: expValEquals('platform_editor_paste_actions_menu', 'isEnabled', true),
				aiSuggestions:
					isEditorAIPluginEnabled && options.isRovoLLMEnabled &&
					expVal('platform_editor_ai_content_suggested_edits', 'isEnabled', false),
				aiSpeechToText:
					isEditorAIPluginEnabled &&
					expValEquals('ai_speech_to_text_in_editor', 'isEnabled', true),
				collabEdit: true,
			},
		})
			.add(ssrPlugin)
			// When hiding content titles in URLs, skip the heading anchor links plugin to prevent generating anchor URLs that expose UGC.
			.maybeAdd(
				[
					EditorHeadingLinksPlugin,
					{
						getHeadingLinkPath: options.getHeadingLinkPath,
					},
				],
				!options.shouldHideHeadingAnchorLinks,
			)
			.add(pageTitleUtilsPlugin)
			.add(fabricExtensionManagerPlugin)
			.add(forgeEditorPlugin)
			.maybeAdd(
				[editorOfflineStoragePlugin, { flags: options.flags }],
				options.isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false),
			)
			.maybeAdd(
				[
					conflictResolutionUIPlugin,
					{
						flags: options.flags,
						contentId: options.contentId,
						spaceKey: options.spaceKey,
					},
				],
				options.isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false),
			)
			.add(emptyStatePlugin)
			.add(legacyContentMacroPlugin)
			.maybeAdd(nativeTabsToolbarPlugin, fg('confluence_frontend_native_tabs_extension'))
			// `previewComponent` is intentionally omitted: the preview view is
			// disabled in the toggle UI today and not yet implemented in Confluence.
			// When it lands, supply a Confluence renderer here (it needs product
			// providers — SmartCard, Emoji, media feature flags — so it can't live
			// in the platform plugin).
			.maybeAdd(
				[
					markdownModePlugin,
					{
						isMarkdownMode,
						contentId: options.contentId ?? '',
						viewModePersistence: {
							getInitialView: readStoredMarkdownViewMode,
							onViewChange: writeStoredMarkdownViewMode,
						},
						// EDITOR-6636 — pass through the Confluence-supplied
						// `serializeFileMedia`. Builds tenant attachment-proxy URLs
						// (`/wiki/download/attachments/...`) for `media(type:file)`
						// so the markdown text remains credential-free and the
						// renderer auths the bytes via the viewer's session cookie.
						serializeFileMedia: options.serializeFileMediaForMarkdown,
						onSourceWritebackError: options.onSourceWritebackError,
					},
				],
				isMarkdownMode,
			)
			.maybeAdd(mediaEditingPlugin, expVal('platform_editor_add_image_editing', 'isEnabled', false))
			.maybeAdd(companyHubSectionCtaPlugin, options.isCompanyHub)
			.maybeAdd(
				[
					malleableUiPlugin,
					{
						__isRemixHidden: options.__isRemixHidden || isMarkdownMode,
						__isRovoLLMEnabled: options.isRovoLLMEnabled,
						__remixDisabledReason: options.__remixDisabledReason,
						__livePage: options.__livePage,
						shouldHideToolbarWhileRemixing: true,
						createRemixContentComponent,
						diagramPaste:
							options.cloudId && options.spaceKey
								? createConfluenceMauiDiagramPasteConfig({
										contentId: options.contentId,
										contentType: options.contentType,
										duplicateMauiApp: options.duplicateMauiApp,
										siteId: options.cloudId,
										spaceKey: options.spaceKey,
										userId: options.currentUserId,
									})
								: undefined,
					},
				],
				UNSAFE_expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true) &&
					!options.isEmbeddedConfluence,
			)
			.maybeAdd(
				[nativeEmbedsPlugin],
				UNSAFE_expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true) ||
					fg('platform_native_embeds_rollout_non_maui_experience'),
			)
			.maybeAdd(
				aiAutocompletePlugin,
				expVal('platform_editor_ai_headingautocomplete', 'isEnabled', false),
			)
	);
}
